<div class="content-info bulk-add">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
    <div class="d-flex align-items-center gap-3">
      <h3 class="mb-0">Bulk EAN Mapping</h3>
      <span class="badge rounded-pill text-bg-secondary" title="Barcodes are tagged independently of pricing">
        pricing not touched
      </span>
    </div>

    <div class="d-flex flex-wrap align-items-center gap-2 toolbar">
      <a class="btn btn-link text-decoration-none px-1" [routerLink]="['/product-list']">
        <i class="bi bi-arrow-left"></i> Product List
      </a>
      <button type="button" class="btn btn-outline-secondary" (click)="addRows(10)">
        <i class="bi bi-plus-lg"></i> 10 rows
      </button>
      <button type="button" class="btn btn-outline-danger" (click)="clearGrid()">
        <i class="bi bi-eraser"></i> Clear
      </button>
      <button
        type="button"
        class="btn btn-success text-nowrap"
        (click)="save()"
        [disabled]="saving || validating"
      >
        @if (saving) {
          <span class="spinner-border spinner-border-sm me-1"></span> Saving…
        } @else if (validating) {
          <span class="spinner-border spinner-border-sm me-1"></span> Checking…
        } @else {
          <i class="bi bi-save"></i> Save
          @if (filledRowCount) {
            ({{ filledRowCount }})
          }
        }
      </button>
    </div>
  </div>

  <p class="text-muted small mb-2">
    One row per barcode. Pick the product in the <strong>SKU</strong> cell
    (search by name or SKU code, or scan it) and type the
    <strong>New EAN</strong>; the product name and its existing barcodes fill in
    automatically. The same SKU may appear on several rows (one per barcode).
    Paste a "SKU&nbsp;→&nbsp;EAN" block from Excel into the SKU cell to fill
    several rows at once, and use <i class="bi bi-arrow-bar-down"></i> on a header
    to copy a cell down. The new code becomes the SKU's
    <strong>primary</strong> barcode (the one shown on lists, labels and
    reports) unless you set Make primary to No; every active barcode scans at
    the till regardless. Use <strong>Retire EAN</strong> to replace a wrong
    barcode in the same save. Empty rows are ignored. All rows are saved
    together — nothing changes until every row is valid. Prices are not
    affected; a SKU without a price is priced at its first GRN.
  </p>

  @if (lastResult) {
    <div class="alert alert-success d-flex align-items-start gap-2 py-2">
      <i class="bi bi-check-circle-fill mt-1"></i>
      <div class="flex-grow-1">
        <strong>Saved.</strong>
        {{ lastResult.created }} new barcode(s)
        @if (lastResult.reactivated) { · {{ lastResult.reactivated }} reactivated }
        @if (lastResult.retired) { · {{ lastResult.retired }} retired }
        @if (lastResult.unchanged) { · {{ lastResult.unchanged }} already tagged }
      </div>
      <button type="button" class="btn btn-sm btn-outline-success text-nowrap" (click)="goToList()">
        View in Product List
      </button>
      <button type="button" class="btn-close" aria-label="Dismiss" (click)="lastResult = null"></button>
    </div>
  }

  <div class="grid-wrap">
    <table class="table table-bordered table-sm grid-table mb-0" [style.width.px]="tableWidth">
      <colgroup>
        <col class="col-row-num" />
        @for (col of columns; track col.key) {
          <col [style.width.px]="col.width" />
        }
        <col class="col-row-actions" />
      </colgroup>
      <thead>
        <tr>
          <th class="row-num sticky-left">#</th>
          @for (col of columns; track col.key) {
            <th
              [class.sticky-left]="frozenLeft[col.key] != null"
              [class.sticky-left-edge]="col.key === lastFrozenKey"
              [style.position]="frozenLeft[col.key] != null ? 'sticky' : null"
              [style.left.px]="frozenLeft[col.key] ?? null"
              [title]="col.hint || ''"
            >
              <div class="d-flex align-items-center justify-content-between gap-1">
                <span class="text-truncate">
                  {{ col.label }}
                  @if (col.required) {
                    <span class="text-danger">*</span>
                  }
                </span>
                @if (col.type !== "auto") {
                  <button
                    type="button"
                    class="btn btn-link btn-fill p-0"
                    tabindex="-1"
                    [title]="'Copy ' + col.label + ' down from the selected row'"
                    (click)="fillDown(col)"
                  >
                    <i class="bi bi-arrow-bar-down"></i>
                  </button>
                }
              </div>
            </th>
          }
          <th class="row-actions sticky-right"></th>
        </tr>
      </thead>
      <tbody>
        @for (row of rows; track row.uid; let ri = $index) {
          <tr [class.row-has-error]="(row.errors | keyvalue).length" [class.row-empty]="isRowEmpty(row)">
            <td class="row-num sticky-left">{{ ri + 1 }}</td>
            @for (col of columns; track col.key) {
              <td
                class="cell"
                [class.has-error]="row.errors[col.key]"
                [class.has-warning]="!row.errors[col.key] && row.warnings[col.key]"
                [class.sticky-left]="frozenLeft[col.key] != null"
                [class.sticky-left-edge]="col.key === lastFrozenKey"
                [style.position]="frozenLeft[col.key] != null ? 'sticky' : null"
                [style.left.px]="frozenLeft[col.key] ?? null"
                [attr.data-cell]="ri + ':' + col.key"
                [title]="row.errors[col.key] || row.warnings[col.key] || ''"
              >
                @switch (col.type) {
                  @case ("sku") {
                    <!-- Searchable by product name or SKU; rows show both.
                         A multi-line paste on the cell still fills the grid
                         (the paste bubbles up from ng-select's input). -->
                    <ng-select
                      class="cell-select"
                      [items]="row.skuItems"
                      bindLabel="sku"
                      bindValue="sku"
                      [searchFn]="matchAll"
                      [loading]="row.skuLoading"
                      [clearable]="true"
                      [searchable]="true"
                      appendTo="body"
                      [placeholder]="col.placeholder || ''"
                      notFoundText="No product matches"
                      typeToSearchText="Type a name or SKU"
                      [(ngModel)]="row.values[col.key]"
                      (search)="onSkuSearch($event, row)"
                      (change)="onSkuSelected($event, row)"
                      (focus)="setActive(ri, col.key)"
                      (paste)="onPaste($event, ri, col.key)"
                    >
                      <ng-template ng-option-tmp let-item="item">
                        <div class="text-truncate fw-semibold">{{ item.longDescription || item.sku }}</div>
                        <div class="small text-muted">SKU {{ item.sku }}</div>
                      </ng-template>
                      <ng-template ng-label-tmp let-item="item">
                        <span class="fw-semibold">{{ item.sku }}</span>
                        @if (item.longDescription) {
                          <span class="text-muted"> · {{ item.longDescription }}</span>
                        }
                      </ng-template>
                    </ng-select>
                  }
                  @case ("text") {
                    <input
                      type="text"
                      class="cell-input"
                      [placeholder]="col.placeholder || ''"
                      [maxlength]="col.maxLength || null"
                      [(ngModel)]="row.values[col.key]"
                      (ngModelChange)="onCellChange(row, col.key)"
                      (focus)="setActive(ri, col.key)"
                      (paste)="onPaste($event, ri, col.key)"
                      (keydown.enter)="moveDown($event, ri, col.key)"
                      autocomplete="off"
                    />
                  }
                  @case ("bool") {
                    <select
                      class="cell-input"
                      [(ngModel)]="row.values[col.key]"
                      (ngModelChange)="onCellChange(row, col.key)"
                      (focus)="setActive(ri, col.key)"
                      (keydown.enter)="moveDown($event, ri, col.key)"
                    >
                      @for (o of boolOptions; track o.name) {
                        <option [ngValue]="o.id">{{ o.name }}</option>
                      }
                    </select>
                  }
                  @case ("auto") {
                    @if (col.key === "name") {
                      <span class="text-truncate d-block small">
                        @if (row.lookingUp) {
                          <span class="spinner-border spinner-border-sm"></span>
                        } @else if (row.lookup?.found) {
                          {{ row.lookup?.name }}
                          @if (row.lookup?.hasPrice === false) {
                            <span class="badge text-bg-warning ms-1" title="No price yet — priced at first GRN">price pending</span>
                          }
                          @if (row.lookup?.isActive === false) {
                            <span class="badge text-bg-secondary ms-1">inactive</span>
                          }
                        } @else if (row.lookup && !row.lookup.found) {
                          <span class="text-danger">not found</span>
                        }
                      </span>
                    } @else {
                      <span class="text-truncate d-block small text-muted" [title]="existingLabel(row)">
                        {{ existingLabel(row) || (row.lookup?.found ? "— none —" : "") }}
                      </span>
                    }
                  }
                }
              </td>
            }
            <td class="row-actions sticky-right text-nowrap">
              <button
                type="button"
                class="btn btn-link p-0 me-1 text-secondary"
                tabindex="-1"
                title="Add another barcode for this SKU"
                (click)="duplicateRow(ri)"
              >
                <i class="bi bi-files"></i>
              </button>
              <button type="button" class="btn btn-link p-0 text-danger" tabindex="-1" title="Remove row" (click)="removeRow(ri)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <div class="d-flex align-items-center gap-2 mt-2">
    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="addRows(10)">
      <i class="bi bi-plus-lg"></i> Add 10 rows
    </button>
    <span class="text-muted small">{{ rows.length }} rows on the grid · {{ filledRowCount }} filled</span>
  </div>

  @if (errorList.length) {
    <div class="card mt-3 border-danger error-panel">
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bi bi-exclamation-octagon-fill text-danger"></i>
        <strong>{{ errorList.length }} problem(s) to fix</strong>
        <span class="text-muted small">— click one to jump to the cell. Nothing is saved until the list is empty.</span>
      </div>
      <ul class="list-unstyled mb-0 error-list">
        @for (e of errorList; track $index) {
          <li>
            <a href="javascript:void(0)" (click)="focusCell(e.row, e.column)">
              <span class="badge text-bg-light border">Row {{ e.row }}</span>
              <span class="fw-semibold">{{ columnLabel(e.column) }}:</span>
              {{ e.error }}
            </a>
          </li>
        }
      </ul>
    </div>
  }

  @if (warningList.length) {
    <div class="card mt-3 border-warning error-panel">
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bi bi-exclamation-triangle-fill text-warning"></i>
        <strong>{{ warningList.length }} warning(s)</strong>
        <span class="text-muted small">— these rows can still be saved.</span>
      </div>
      <ul class="list-unstyled mb-0 error-list">
        @for (w of warningList; track $index) {
          <li>
            <a href="javascript:void(0)" (click)="focusCell(w.row, w.column)">
              <span class="badge text-bg-light border">Row {{ w.row }}</span>
              <span class="fw-semibold">{{ columnLabel(w.column) }}:</span>
              {{ w.error }}
            </a>
          </li>
        }
      </ul>
    </div>
  }
</div>
